import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import TagFilterBar from "./Components/TagFilterBar";
import { parseTags, questionMatchesSteps } from "./Components/TagFilterUtils";
import Spinner from "../../globalComponents/ui/Spinner";
import SimpleDropdown from "../../globalComponents/form/dropdowns/SimpleDropdown";
import SearchableDropdown from "../../globalComponents/form/dropdowns/SearchableDropdown";
import ViewOnlyBanner from "./Components/ViewOnlyBanner";
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import KpiRequestModal from "../DataManagment/Quantative/component/KpiRequestModal";
import KpiRequestStatusModal from "../DataManagment/Quantative/component/KpiRequestStatusModal";

// ─── UI atoms ─────────────────────────────────────────────────────────────────



function Badge({ children, color = "#64748b", bg = "#f1f5f9", border = "#e2e8f0" }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
      background: bg, color, border: `1px solid ${border}`, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ─── Factor Change Warning Modal ──────────────────────────────────────────────

function FactorWarningModal({ unitName, onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "min(400px, 94vw)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        {/* Icon strip */}
        <div style={{ background: "#fffbeb", padding: "20px 24px 16px", borderBottom: "1px solid #fef3c7", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>⚠</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
              Editing conversion factor
            </div>
            <div style={{ fontSize: 12, color: "#b45309", lineHeight: 1.5 }}>
              Changing the factor for <strong>"{unitName}"</strong> will affect how historical data
              is converted and displayed across all reports.
            </div>
          </div>
        </div>

        <div style={{ padding: "16px 24px 20px" }}>
          <p style={{ fontSize: 12, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
            This action may cause inconsistencies in previously recorded values.
            Are you sure you want to continue?
          </p>
        </div>

        <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "1.5px solid #e2e8f0",
              background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#64748b",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none",
              background: "#d97706", color: "#fff",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
            }}
          >
            Yes, edit factor
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── Unit Factor History Modal ────────────────────────────────────────────────

function UnitFactorHistoryModal({ kpiId, unitId, unitName, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchHistory = async () => {
      try {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getUnitFactorHistory`,
          {},
          { kpiId, unitId },
          "GET",
          false,
          false
        );
        if (alive && isSuccess && data?.data) {
          setHistory(data.data);
        }
      } catch (_) {}
      finally {
        if (alive) setLoading(false);
      }
    };
    fetchHistory();
    return () => { alive = false; };
  }, [kpiId, unitId]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, width: "min(460px, 94vw)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden",
        maxHeight: "80vh", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Factor Override History</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>For unit "{unitName}"</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 18, color: "#94a3b8", cursor: "pointer" }}>×</button>
        </div>

        {/* List */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ padding: "40px 0", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <Spinner size={20} color="#3f88a5" />
              <span style={{ fontSize: 12, color: "#94a3b8" }}>Loading history…</span>
            </div>
          ) : history.length === 0 ? (
            <div style={{ padding: "40px 10px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
              No override history found for this unit.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {history.map((row) => {
                const rowId = row.id ?? row.id;
                const oldFactor = row.oldFactor ?? row.old_factor ?? row.oldfactor;
                const newFactor = row.newFactor ?? row.new_factor ?? row.newfactor;
                const createdAt = row.createdAt ?? row.created_at ?? row.createdat;
                const firstName = row.firstName ?? row.first_name ?? row.firstname;
                const lastName = row.lastName ?? row.last_name ?? row.lastname;
                const email = row.email ?? row.email;
                return (
                  <div key={rowId} style={{
                    padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8,
                    background: "#f8fafc", fontSize: 12,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: "#334155" }}>
                        {firstName ? `${firstName} ${lastName || ""}` : email || "System"}
                      </span>
                      <span style={{ color: "#94a3b8", fontSize: 10.5 }}>
                        {new Date(createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit"
                        })}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#475569" }}>
                      <span>Factor changed:</span>
                      <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>{oldFactor}</span>
                      <span>→</span>
                      <span style={{ fontWeight: 700, color: "#0369a1" }}>{newFactor}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "7px 18px", borderRadius: 8, border: "1.5px solid #e2e8f0",
              background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#64748b",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}



// ─── Unit Editor Modal ────────────────────────────────────────────────────────
// platformBaseUnitId → locked disabled input, factor always locked
// selectedUnitId     → user's display unit, fully selectable
// Changing any non-base factor triggers a confirmation warning

function UnitEditorModal({ kpi, onClose, onSave, onRequestUnit, refreshKey }) {
  const [localUnits, setLocalUnits] = useState(() =>
    (kpi.supportedUnits || []).map(u => ({ ...u }))
  );
  const [selectedUnitId, setSelectedUnitId] = useState(kpi.selectedUnitId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [historyUnit, setHistoryUnit] = useState(null);

  // Master unit list and fetching states
  const [allMasterUnits, setAllMasterUnits] = useState([]);
  const [fetchingMaster, setFetchingMaster] = useState(false);
  const [newUnitId, setNewUnitId] = useState("");
  const [newUnitFactor, setNewUnitFactor] = useState("");

  useEffect(() => {
    let alive = true;
    const fetchMasterUnits = async () => {
      setFetchingMaster(true);
      try {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getUnitMaster`,
          {},
          {},
          "GET",
          false,
          false
        );
        if (alive && isSuccess && Array.isArray(data?.data)) {
          setAllMasterUnits(data.data);
        }
      } catch (_) {}
      finally {
        if (alive) setFetchingMaster(false);
      }
    };
    fetchMasterUnits();
    return () => { alive = false; };
  }, []);

  const handleAddUnit = () => {
    if (!newUnitId) return;
    const selectedMaster = allMasterUnits.find(mu => String(mu.id) === String(newUnitId));
    if (!selectedMaster) return;
    const factorNum = Number(newUnitFactor);
    if (!newUnitFactor || isNaN(factorNum) || factorNum <= 0) {
      setError("Please enter a valid factor for the new unit.");
      return;
    }
    setError(null);
    setLocalUnits(prev => {
      const existsIndex = prev.findIndex(u => String(u.unit_id) === String(selectedMaster.id));
      if (existsIndex > -1) {
        const next = [...prev];
        next[existsIndex] = { ...next[existsIndex], factor: newUnitFactor };
        return next;
      }
      return [
        ...prev,
        {
          unit_id: selectedMaster.id,
          unit: selectedMaster.unit,
          unit_name: selectedMaster.unit_name,
          factor: newUnitFactor
        }
      ];
    });
    setNewUnitId("");
    setNewUnitFactor("");
  };

  // Warning state: if an existing unit's factor is modified, warn only on save click
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [warningUnitName, setWarningUnitName] = useState("");

  // Pending / approved unit requests for THIS KPI
  const [unitRequests, setUnitRequests] = useState([]);
  const [fetchingRequests, setFetchingRequests] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchRequests = async () => {
      setFetchingRequests(true);
      try {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/getRequestedKpisStatus`,
          {},
          { request_type: "UNIT", kpiId: kpi.kpiId },
          "GET",
          false,
          false
        );
        if (alive && isSuccess) {
          const list = Array.isArray(data?.data) ? data.data : [];
          // Show: requests linked to THIS kpi_id, OR legacy requests with null kpi_id
          // Only show PENDING (awaiting approval) — remove REJECTED & APPROVED ones from view
          setUnitRequests(
            list.filter(r => {
              if (r.status === "REJECTED" || r.status === "APPROVED") return false;
              const linked = r.kpi_id != null && String(r.kpi_id) === String(kpi.kpiId);
              const unlinked = r.kpi_id == null; // legacy requests without kpi link
              return linked || unlinked;
            })
          );
        }
      } catch (_) { /* silently ignore */ }
      finally { if (alive) setFetchingRequests(false); }
    };
    fetchRequests();
    return () => { alive = false; };
  }, [kpi.kpiId, refreshKey]);

  const baseUnit = localUnits.find(u => u.unit_id === kpi.platformBaseUnitId);

  const handleFactorChange = (unit_id, unitName, value) => {
    setLocalUnits(prev => prev.map(u =>
      u.unit_id === unit_id
        ? { ...u, factor: value }
        : u
    ));
  };

  const validate = () => {
    for (const u of localUnits) {
      if (u.unit_id === kpi.platformBaseUnitId) continue;
      const f = Number(u.factor);
      if (u.factor === "" || isNaN(f) || f <= 0)
        return `Factor for "${u.unit ?? u.unit_id}" must be a positive number.`;
    }
    return null;
  };

  const executeSave = async () => {
    setSaving(true);
    const cleaned = localUnits.map(u => ({
      unit_id: u.unit_id,
      unit: u.unit,
      factor: Number(u.factor),
    }));
    await onSave({
      kpiId: kpi.kpiId,
      platformBaseUnitId: kpi.platformBaseUnitId,
      selectedUnitId,
      supportedUnits: cleaned,
    });
    setSaving(false);
    setShowSaveWarning(false);
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);

    const originalUnitsMap = new Map((kpi.supportedUnits || []).map(u => [u.unit_id, Number(u.factor)]));
    let hasFactorChanges = false;
    let changedUnitName = "";

    for (const u of localUnits) {
      if (u.unit_id === kpi.platformBaseUnitId) continue;
      if (!originalUnitsMap.has(u.unit_id)) continue;

      const originalFactor = originalUnitsMap.get(u.unit_id);
      if (originalFactor !== undefined && Number(u.factor) !== originalFactor) {
        hasFactorChanges = true;
        changedUnitName = u.unit ?? `Unit #${u.unit_id}`;
        break;
      }
    }

    if (hasFactorChanges) {
      setWarningUnitName(changedUnitName);
      setShowSaveWarning(true);
    } else {
      await executeSave();
    }
  };

  const disabledInputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1.5px solid #e2e8f0", fontSize: 13,
    background: "#f8fafc", color: "#94a3b8",
    cursor: "not-allowed", outline: "none",
    fontWeight: 500,
  };

  // Status colour helpers for requested units
  const reqStatusStyle = (status) => {
    if (status === "APPROVED") return { dot: "#10b981", text: "#065f46", bg: "#ecfdf5", border: "#a7f3d0", label: "Approved" };
    if (status === "REJECTED") return { dot: "#ef4444", text: "#991b1b", bg: "#fef2f2", border: "#fecaca", label: "Rejected" };
    return { dot: "#f59e0b", text: "#92400e", bg: "#fffbeb", border: "#fde68a", label: "Pending" };
  };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "#fff", borderRadius: 14, width: "min(520px, 96vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

          {/* Header */}
          <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
                {kpi.title}
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>Configure selected unit &amp; conversion factors</div>
            </div>
            <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 18, color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: "2px 4px", flexShrink: 0 }}>×</button>
          </div>

          <div style={{ overflowY: "auto", padding: "18px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Platform Base Unit — disabled input field */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Platform Base Unit
              </label>
              <input
                disabled
                value={baseUnit?.unit ?? `Unit #${kpi.platformBaseUnitId}`}
                style={disabledInputStyle}
              />
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
                This is set by the platform and cannot be changed.
              </div>
            </div>

            {/* Selected Unit */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Selected Unit
              </label>
              <div style={{ position: "relative", maxWidth: 280 }}>
                <select
                  value={selectedUnitId ?? ""}
                  onChange={e => setSelectedUnitId(Number(e.target.value))}
                  style={{
                    width: "100%", padding: "8px 32px 8px 10px", borderRadius: 8,
                    border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none",
                    background: "#fff", color: "#1e293b", appearance: "none", cursor: "pointer",
                  }}
                  onFocus={e => e.target.style.borderColor = "#93c5fd"}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                >
                  {localUnits.map(u => (
                    <option key={u.unit_id} value={u.unit_id}>{u.unit ?? `Unit #${u.unit_id}`}</option>
                  ))}
                  {unitRequests.length > 0 && (
                    <optgroup label="── Requested (not yet available) ──">
                      {unitRequests.map(r => (
                        <option
                          key={`req-opt-${r.id}`}
                          value={`req-${r.id}`}
                          disabled
                          style={{ color: "#94a3b8" }}
                        >
                          {r.title} [{r.status === "APPROVED" ? "Approved" : r.status === "REJECTED" ? "Rejected" : "Pending"}]
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <span style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  pointerEvents: "none", fontSize: 10, color: "#94a3b8", lineHeight: 1,
                }}>▾</span>
              </div>
            </div>

            {/* Conversion factor table */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Conversion Factors
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "visible", position: "relative" }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", padding: "8px 14px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                  {["Unit", "Factor"].map((h, i) => (
                    <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</span>
                  ))}
                </div>

              {/* Existing platform units */}
                {localUnits.map((u, idx) => {
                  const isBase = u.unit_id === kpi.platformBaseUnitId;
                  const isSelected = u.unit_id === selectedUnitId;
                  const isLastPlatform = idx === localUnits.length - 1 && unitRequests.length === 0;

                  return (
                    <div key={u.unit_id} style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr",
                      padding: "10px 14px", borderBottom: isLastPlatform ? "none" : "1px solid #f8fafc",
                      alignItems: "center",
                      background: isBase ? "#fafafa" : "#fff",
                    }}>
                      {/* Unit name + badges */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: isBase ? "#94a3b8" : "#1e293b" }}>
                          {u.unit ?? `#${u.unit_id}`}
                        </span>
                        {isBase && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "#e2e8f0", color: "#64748b" }}>BASE</span>
                        )}
                        {isSelected && !isBase && (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: "#dbeafe", color: "#1d4ed8" }}>SELECTED</span>
                        )}
                      </div>

                      {/* Factor cell */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {isBase ? (
                          <input
                            disabled
                            value={u.factor}
                            style={{
                              width: "80%", padding: "6px 8px", borderRadius: 7,
                              border: "1.5px solid #e2e8f0",
                              background: "#f1f5f9", color: "#94a3b8",
                              fontSize: 13, fontWeight: 500,
                              cursor: "not-allowed", outline: "none",
                            }}
                          />
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, width: "80%" }}>
                            <input
                              type="number"
                              step="any"
                              min="0.000001"
                              value={u.factor}
                              onChange={e => handleFactorChange(u.unit_id, u.unit ?? `Unit #${u.unit_id}`, e.target.value)}
                              style={{
                                flex: 1, padding: "6px 8px", borderRadius: 7,
                                border: "1.5px solid #e2e8f0",
                                fontSize: 13, outline: "none",
                                background: "#fff", color: "#1e293b",
                                cursor: "text",
                                minWidth: 0,
                              }}
                              onFocus={e => e.target.style.borderColor = "#3f88a5"}
                              onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                            />
                            <button
                              onClick={() => setHistoryUnit(u)}
                              title="View Factor Override History"
                              style={{
                                display: "flex", alignItems: "center", justifyContent: "center",
                                width: 28, height: 28, borderRadius: 6, border: "1px solid #e2e8f0",
                                background: "#fff", cursor: "pointer", color: "#64748b",
                                flexShrink: 0,
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.color = "#3f88a5";
                                e.currentTarget.style.borderColor = "#3f88a5";
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.background = "#fff";
                                e.currentTarget.style.color = "#64748b";
                                e.currentTarget.style.borderColor = "#e2e8f0";
                              }}
                            >
                              🕒
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* ── Loading row while fetching unit requests ── */}
                {fetchingRequests && (
                  <div style={{
                    padding: "8px 14px", borderTop: "1px solid #f1f5f9",
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#fafafa",
                  }}>
                    <Spinner size={10} color="#94a3b8" />
                    <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>Loading unit requests…</span>
                  </div>
                )}

                {/* ── Requested Units section divider ── */}
                {!fetchingRequests && unitRequests.length > 0 && (
                  <div style={{
                    padding: "6px 14px",
                    background: "#f8fafc",
                    borderTop: "1px solid #e2e8f0",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Requested Units
                    </span>
                    <span style={{ fontSize: 9.5, color: "#94a3b8" }}>(locked until approved)</span>
                  </div>
                )}

                {/* Requested units (pending / approved / rejected) */}
                {!fetchingRequests && unitRequests.map((req, idx) => {
                  const sc = reqStatusStyle(req.status);
                  const isLast = idx === unitRequests.length - 1;
                  return (
                    <div key={`req-${req.id}`} style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr",
                      padding: "10px 14px",
                      borderBottom: isLast ? "none" : "1px solid #f8fafc",
                      alignItems: "center",
                      background: sc.bg,
                      opacity: req.status === "REJECTED" ? 0.6 : 1,
                    }}>
                      {/* Requested unit name + status badge */}
                      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: sc.text }}>
                          {req.title}
                        </span>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 5,
                          background: "#fff", color: sc.text, border: `1px solid ${sc.border}`,
                          whiteSpace: "nowrap",
                        }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: sc.dot, flexShrink: 0 }} />
                          {sc.label}
                        </span>
                      </div>

                      {/* Locked indicator */}
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>🔒</span>
                        <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                          {req.status === "APPROVED" ? "Awaiting setup" : "Awaiting approval"}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* ── Add New Unit factor mapping inline row ── */}
                {allMasterUnits.length > 0 && (
                  <div style={{
                    display: "grid", gridTemplateColumns: "1.2fr 1fr",
                    padding: "12px 14px",
                    background: "#f8fafc",
                    borderTop: "1.5px dashed #e2e8f0",
                    alignItems: "flex-end",
                    gap: 12
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>Available Units</span>
                      <SearchableDropdown
                        value={newUnitId}
                        onChange={val => setNewUnitId(val)}
                        options={allMasterUnits.map(mu => ({
                          value: mu.id,
                          label: mu.unit_name ? `${mu.unit_name} (${mu.unit})` : mu.unit
                        }))}
                        placeholder="— Select Unit —"
                        searchPlaceholder="Search units..."
                        dropDirection="up"
                      />
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>Factor</span>
                        <input
                          type="number"
                          step="any"
                          min="0.000001"
                          placeholder="e.g. 1.0"
                          value={newUnitFactor}
                          onChange={e => setNewUnitFactor(e.target.value)}
                          style={{
                            width: "100%", padding: "6px 8px", borderRadius: 8,
                            border: "1.5px solid #cbd5e1",
                            fontSize: 13, outline: "none",
                            background: "#fff", color: "#1e293b"
                          }}
                          onFocus={e => e.target.style.borderColor = "#3f88a5"}
                          onBlur={e => e.target.style.borderColor = "#cbd5e1"}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddUnit}
                        disabled={!newUnitId || !newUnitFactor}
                        style={{
                          padding: "0 16px", height: 31, borderRadius: 8, border: "none",
                          background: (!newUnitId || !newUnitFactor) ? "#cbd5e1" : "#3f88a5",
                          color: "#fff", fontSize: 12.5, fontWeight: 700,
                          cursor: (!newUnitId || !newUnitFactor) ? "default" : "pointer",
                          boxShadow: (!newUnitId || !newUnitFactor) ? "none" : "0 2px 6px rgba(63,136,165,0.2)"
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <p style={{ fontSize: 11, color: "#94a3b8", margin: "6px 0 0" }}>
                Factors represent how many base units equal one of the given unit.
              </p>
            </div>

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626" }}>
                {error}
              </div>
            )}

            {/* ── Request a custom unit — compact inline row at the bottom ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", borderRadius: 8,
              background: "#f8fafc", border: "1px solid #e2e8f0",
              marginTop: 2,
            }}>
              <span style={{ fontSize: 11.5, color: "#64748b", flex: 1 }}>
                💡 Need a unit not listed above?
              </span>
              <button
                type="button"
                onClick={onRequestUnit}
                style={{
                  padding: "4px 11px", borderRadius: 6, flexShrink: 0,
                  border: "1px solid #3f88a5", background: "transparent",
                  color: "#3f88a5", fontSize: 11.5, fontWeight: 700,
                  cursor: "pointer", whiteSpace: "nowrap",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#e8f4f8"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                + Request Unit
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#64748b" }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: saving ? "#e2e8f0" : "#3f88a5",
              color: saving ? "#94a3b8" : "#fff",
              cursor: saving ? "default" : "pointer", fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
              boxShadow: saving ? "none" : "0 2px 8px rgba(63,136,165,0.3)",
            }}>
              {saving ? <><Spinner size={12} color="#94a3b8" /> Saving…</> : "Save Configuration"}
            </button>
          </div>
        </div>
      </div>

      {historyUnit && (
        <UnitFactorHistoryModal
          kpiId={kpi.kpiId}
          unitId={historyUnit.unit_id}
          unitName={historyUnit.unit}
          onClose={() => setHistoryUnit(null)}
        />
      )}

      {showSaveWarning && (
        <FactorWarningModal
          unitName={warningUnitName}
          onConfirm={executeSave}
          onCancel={() => setShowSaveWarning(false)}
        />
      )}
    </>
  );
}

// ─── Bulk Apply Modal ─────────────────────────────────────────────────────────

function BulkApplyModal({ filteredKpis, onClose, onApply }) {
  const [targetUnitId, setTargetUnitId] = useState("");
  const [applying, setApplying] = useState(false);

  const availableUnits = useMemo(() => {
    const map = new Map();
    for (const kpi of filteredKpis) {
      if (!kpi.hasUnits) continue;
      for (const u of kpi.supportedUnits) {
        if (u.unit_id && !map.has(u.unit_id))
          map.set(u.unit_id, { unit_id: u.unit_id, unit: u.unit });
      }
    }
    return Array.from(map.values());
  }, [filteredKpis]);

  const selectedUnitIdNum = targetUnitId ? Number(targetUnitId) : null;

  const eligibleKpis = useMemo(() => {
    if (!selectedUnitIdNum) return [];
    return filteredKpis.filter(k =>
      k.hasUnits && k.supportedUnits.some(u => u.unit_id === selectedUnitIdNum)
    );
  }, [filteredKpis, selectedUnitIdNum]);

  const handleApply = async () => {
    if (!selectedUnitIdNum || !eligibleKpis.length) return;
    setApplying(true);
    const configs = eligibleKpis.map(k => ({
      kpiId: k.kpiId,
      platformBaseUnitId: k.platformBaseUnitId,
      selectedUnitId: selectedUnitIdNum,
      supportedUnits: k.supportedUnits.map(u => ({ unit_id: u.unit_id, factor: u.factor })),
    }));
    await onApply(configs);
    setApplying(false);
  };

  const kpisWithUnits = filteredKpis.filter(k => k.hasUnits).length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "min(420px, 96vw)", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "18px 24px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Bulk Apply Selected Unit</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
              Apply to {kpisWithUnits} visible KPI{kpisWithUnits !== 1 ? "s" : ""} with units
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 20, color: "#94a3b8", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Select Unit
            </label>
            <div style={{ position: "relative" }}>
              <select
                value={targetUnitId}
                onChange={e => setTargetUnitId(e.target.value)}
                style={{
                  width: "100%", padding: "9px 36px 9px 12px", borderRadius: 8,
                  border: "1.5px solid #e2e8f0", fontSize: 13,
                  outline: "none", background: "#fff", color: "#1e293b",
                  cursor: "pointer", appearance: "none",
                }}
              >
                <option value="">— Choose a unit —</option>
                {availableUnits.map(u => (
                  <option key={u.unit_id} value={u.unit_id}>{u.unit}</option>
                ))}
              </select>
              <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 10, color: "#94a3b8", lineHeight: 1 }}>▾</span>
            </div>
          </div>

          {selectedUnitIdNum && (
            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "12px 16px" }}>
              <div style={{ fontSize: 12, color: "#0c4a6e" }}>
                Will update <strong>{eligibleKpis.length} KPI{eligibleKpis.length !== 1 ? "s" : ""}</strong>
                {eligibleKpis.length < kpisWithUnits && (
                  <span style={{ fontSize: 11, color: "#64748b" }}> — others skipped (unit not supported)</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#64748b" }}>
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedUnitIdNum || !eligibleKpis.length || applying}
            style={{
              padding: "9px 22px", borderRadius: 8, border: "none",
              background: (!selectedUnitIdNum || !eligibleKpis.length) ? "#e2e8f0" : "#3f88a5",
              color: (!selectedUnitIdNum || !eligibleKpis.length) ? "#94a3b8" : "#fff",
              cursor: (!selectedUnitIdNum || !eligibleKpis.length) ? "default" : "pointer",
              fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8,
            }}
          >
            {applying
              ? <><Spinner size={12} color="#94a3b8" /> Applying…</>
              : `Apply to ${eligibleKpis.length} KPI${eligibleKpis.length !== 1 ? "s" : ""}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Row ──────────────────────────────────────────────────────────────────

function KpiUnitRow({ kpi, pendingConfig, onConfigure, onQuickUnitChange, onRequestUnit, isLast, disabled }) {
  const cfg = pendingConfig ?? kpi;
  const isDirty = !disabled && !!pendingConfig;

  if (!kpi.hasUnits) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 14, padding: "11px 18px",
        borderBottom: isLast ? "none" : "1px solid #f1f5f9", background: "#f8fafc",
      }}>
        {/* Title */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
            {kpi.title}
          </div>
        </div>
        
        {/* Badge */}
        <Badge color="#b45309" bg="#fffbeb" border="#fcd34d">No Units</Badge>

        {/* Request Unit button */}
        {!disabled && (
          <button
            onClick={() => onRequestUnit && onRequestUnit(kpi.kpiId, kpi.title)}
            style={{
              padding: "6px 14px", borderRadius: 7, border: "1.5px solid #fcd34d",
              background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#b45309",
              display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#fffbeb"; e.currentTarget.style.borderColor = "#fbbf24"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#fcd34d"; }}
          >
            ✉ Request Unit
          </button>
        )}
      </div>
    );
  }

  const displayUnits = cfg.supportedUnits ?? kpi.supportedUnits;
  const displaySelectedId = cfg.selectedUnitId ?? kpi.selectedUnitId;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "11px 18px",
      borderBottom: isLast ? "none" : "1px solid #f1f5f9",
      background: isDirty ? "#fffbeb" : "#fff", transition: "background 0.1s",
    }}>
      {/* Title */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpi.title}>
          {kpi.title}
        </div>
      </div>

      {/* Quick selected unit selector — no label, with chevron */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <select
          value={displaySelectedId ?? ""}
          onChange={e => onQuickUnitChange(kpi.kpiId, Number(e.target.value))}
          disabled={disabled}
          style={{
            padding: "6px 30px 6px 10px", borderRadius: 7,
            border: `1.5px solid ${isDirty ? "#fcd34d" : "#e2e8f0"}`,
            background: disabled ? "#f8fafc" : (isDirty ? "#fffbeb" : "#fff"),
            fontSize: 12, fontWeight: 600,
            color: disabled ? "#94a3b8" : (isDirty ? "#b45309" : "#1e293b"),
            cursor: disabled ? "not-allowed" : "pointer", outline: "none",
            appearance: "none", minWidth: 80,
          }}
        >
          {displayUnits.map(u => (
            <option key={u.unit_id} value={u.unit_id}>{u.unit}</option>
          ))}
        </select>
        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 10, color: disabled ? "#cbd5e1" : (isDirty ? "#b45309" : "#94a3b8"), lineHeight: 1 }}>▾</span>
      </div>

      {/* Unsaved indicator — plain text, no pill */}
      {isDirty && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "#d97706", flexShrink: 0 }}>
          Unsaved
        </span>
      )}

      {/* Configure button */}
      {!disabled && (
        <button
          onClick={() => onConfigure(kpi)}
          style={{
            padding: "6px 14px", borderRadius: 7, border: "1.5px solid #e2e8f0",
            background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#3f88a5",
            display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#e0f2fe"; e.currentTarget.style.borderColor = "#93c5fd"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
        >
          ⚙ Configure
        </button>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function UnitKpiSettings() {
  const [allKpis, setAllKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [pendingConfigs, setPendingConfigs] = useState(new Map());
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [editingKpi, setEditingKpi] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const [showKpiRequest, setShowKpiRequest] = useState(false);
  const [showKpiStatus, setShowKpiStatus] = useState(false);
  const [requestType, setRequestType] = useState("UNIT");
  const [requestKpiId, setRequestKpiId] = useState(null);
  const [requestKpiTitle, setRequestKpiTitle] = useState(null);
  const [unitRequestsRefreshKey, setUnitRequestsRefreshKey] = useState(0);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fyData = useMemo(() => { try { return JSON.parse(localStorage.getItem("financialYears")) ?? []; } catch { return []; } }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);

  const isViewOnly = selectedFY?.id !== latestFY?.id;

  const fetchKpis = useCallback(async () => {
    if (!selectedFY?.id) return;
    setLoading(true);
    try {
      const [settingsRes, unitSettingsRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getUnitKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
      ]);

      if (unitSettingsRes?.isSuccess && unitSettingsRes?.data?.data) {
        const rawSettings = settingsRes?.data;
        const allUnitKpis = unitSettingsRes.data.data;
        
        let filteredBySettings = allUnitKpis;
        if (rawSettings && Array.isArray(rawSettings.kpiIds)) {
          const enabledKpiIds = new Set(rawSettings.kpiIds.map(Number));
          filteredBySettings = allUnitKpis.filter(k => enabledKpiIds.has(Number(k.kpiId)));
        } else {
          // If no settings saved yet for this FY, default to empty because no KPIs are selected in KPIs settings
          filteredBySettings = [];
        }

        setAllKpis(filteredBySettings);
        setPendingConfigs(new Map());
      } else {
        showToast("Failed to load KPI unit settings", "error");
      }
    } catch {
      showToast("Error loading KPI unit settings", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFY?.id]);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const [kpiView, setKpiView] = useState("list");

  const selectedKpis = useMemo(() => {
    return allKpis.filter(kpi => {
      const cfg = pendingConfigs.get(kpi.kpiId) ?? kpi;
      return cfg.selectedUnitId !== null && cfg.selectedUnitId !== undefined;
    });
  }, [allKpis, pendingConfigs]);

  const selectedKpisCount = selectedKpis.length;

  const savedIds = useMemo(() => {
    const kpis = kpiView === "selected" ? selectedKpis : allKpis;
    return new Set(kpis.map(k => k.kpiId));
  }, [allKpis, selectedKpis, kpiView]);

  const pendingIds = useMemo(() => {
    const kpis = kpiView === "selected" ? selectedKpis : allKpis;
    return new Set(kpis.map(k => k.kpiId));
  }, [allKpis, selectedKpis, kpiView]);

  const filteredKpis = useMemo(() => {
    const source = kpiView === "selected" ? selectedKpis : allKpis;
    let list = source.filter(k => questionMatchesSteps(k, filterSteps));
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(k => k.title?.toLowerCase().includes(s));
    }
    return list;
  }, [allKpis, selectedKpis, kpiView, filterSteps, search]);

  const displayKpis = filteredKpis;

  const handleQuickUnitChange = (kpiId, newSelectedUnitId) => {
    const kpi = allKpis.find(k => k.kpiId === kpiId);
    if (!kpi) return;
    const base = pendingConfigs.get(kpiId) ?? kpi;
    setPendingConfigs(prev => new Map(prev).set(kpiId, {
      kpiId,
      platformBaseUnitId: base.platformBaseUnitId,
      selectedUnitId: newSelectedUnitId,
      supportedUnits: base.supportedUnits,
    }));
  };

  const handleModalSave = async (cfg) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitKpiSettings`,
        {},
        { configs: [cfg] },
        "POST"
      );
      if (isSuccess) {
        await fetchKpis();
        setPendingConfigs(prev => { const n = new Map(prev); n.delete(cfg.kpiId); return n; });
        setEditingKpi(null);
        showToast("Unit configuration saved");
      } else {
        showToast(data?.message || "Failed to save", "error");
      }
    } catch {
      showToast("Error saving configuration", "error");
    }
  };

  const handleBulkApply = async (configs) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitKpiSettings`,
        {},
        { configs },
        "POST"
      );
      if (isSuccess) {
        await fetchKpis();
        setPendingConfigs(prev => { const n = new Map(prev); configs.forEach(c => n.delete(c.kpiId)); return n; });
        setShowBulkModal(false);
        showToast(`${configs.length} KPI${configs.length !== 1 ? "s" : ""} updated`);
      } else {
        showToast(data?.message || "Bulk apply failed", "error");
      }
    } catch {
      showToast("Error during bulk apply", "error");
    }
  };

  const handleSaveAll = async () => {
    if (!pendingConfigs.size) return;
    setSaving(true);
    const configs = Array.from(pendingConfigs.values());
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}saveUnitKpiSettings`,
        {},
        { configs },
        "POST"
      );
      if (isSuccess) {
        await fetchKpis();
        setPendingConfigs(new Map());
        showToast(`${configs.length} change${configs.length !== 1 ? "s" : ""} saved`);
      } else {
        showToast(data?.message || "Failed to save", "error");
      }
    } catch {
      showToast("Error saving changes", "error");
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (kpi) => {
    const pending = pendingConfigs.get(kpi.kpiId);
    setEditingKpi(pending ? { ...kpi, ...pending } : kpi);
  };

  const hasPending = pendingConfigs.size > 0;
  const kpisWithUnits = allKpis.filter(k => k.hasUnits).length;

  if (loading) {
    return (
      <div style={{ padding: 100, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI unit settings…</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>
      <style>{`
        @keyframes ukpi-spin { to { transform: rotate(360deg) } }
        @keyframes ukpi-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        select:focus { outline: none; }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10,
          zIndex: 2000, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          animation: "ukpi-toast-in 0.25s ease",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: "linear-gradient(to bottom, #3f88a5, #2563eb)" }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>KPI Unit Settings</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {allKpis.length} KPI{allKpis.length !== 1 ? "s" : ""} · {kpisWithUnits} with units
              {hasPending && !isViewOnly && (
                <span style={{ color: "#d97706", fontWeight: 600 }}>
                  {" "}· {pendingConfigs.size} unsaved
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SimpleDropdown
            options={fyData.map(fy => ({
              value: fy.id,
              label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}`,
            }))}
            value={selectedFY?.id || ""}
            onChange={val => {
              const found = fyData.find(fy => fy.id === Number(val));
              if (found) setSelectedFY({ id: found.id, name: found.financial_year_value, financial_year_value: found.financial_year_value });
            }}
            isAlert={selectedFY?.id !== latestFY?.id}
            style={{ width: 180 }}
          />
          {hasPending && !isViewOnly && (
            <button onClick={() => setPendingConfigs(new Map())} style={{
              padding: "8px 16px", borderRadius: 8, border: "1.5px solid #fecaca",
              background: "#fff5f5", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#ef4444",
            }}>
              Discard
            </button>
          )}
          {!isViewOnly && (
            <button
              onClick={handleSaveAll}
              disabled={!hasPending || saving}
              style={{
                background: hasPending ? "#3f88a5" : "#e2e8f0",
                color: hasPending ? "#fff" : "#94a3b8",
                border: "none", padding: "9px 22px", borderRadius: 8,
                cursor: hasPending ? "pointer" : "default",
                fontSize: 13, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: hasPending ? "0 2px 8px rgba(63,136,165,0.35)" : "none",
              }}
            >
              {saving ? <><Spinner size={12} color="#fff" /> Saving…</> : "Save All"}
            </button>
          )}
          <button
            onClick={() => setShowKpiStatus(true)}
            style={{
              padding: "8px 16px", borderRadius: 8,
              border: "1.5px solid #e2e8f0",
              background: "#fff", cursor: "pointer",
              fontSize: 12, fontWeight: 600,
              color: "#64748b",
            }}
          >
            View Requests
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>

        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, background: "#e2e8f0", padding: 3, borderRadius: 10, width: "fit-content", marginBottom: 14 }}>
          {[
            { key: "list", label: "All KPIs", count: allKpis.length },
            { key: "selected", label: "Selected", count: selectedKpisCount }
          ].map(tab => (
            <button key={tab.key} onClick={() => setKpiView(tab.key)} style={{
              padding: "7px 20px", border: "none", borderRadius: 8,
              background: kpiView === tab.key ? "#fff" : "transparent",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              color: kpiView === tab.key ? "#0f172a" : "#64748b",
              display: "flex", alignItems: "center", gap: 7,
              boxShadow: kpiView === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition: "all 0.15s"
            }}>
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key ? (tab.key === "selected" ? "#dbeafe" : "#f1f5f9") : "#d1d5db",
                color: kpiView === tab.key ? (tab.key === "selected" ? "#1d4ed8" : "#475569") : "#94a3b8"
              }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Filter + search panel */}
        <div style={{ background: "#fff", padding: "16px", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8" }}>⌕</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search KPIs by title…"
                style={{
                  width: "100%", padding: "9px 12px 9px 32px",
                  border: "1.5px solid #e2e8f0", borderRadius: 9,
                  fontSize: 13, outline: "none", color: "#1e293b", background: "#fff",
                }}
                onFocus={e => e.target.style.borderColor = "#93c5fd"}
                onBlur={e => e.target.style.borderColor = "#e2e8f0"}
              />
            </div>
            {displayKpis.filter(k => k.hasUnits).length > 0 && !isViewOnly && (
              <button
                onClick={() => setShowBulkModal(true)}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "1.5px solid #bae6fd",
                  background: "#f0f9ff", cursor: "pointer", fontSize: 12, fontWeight: 700,
                  color: "#0369a1", display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                }}
              >
                ⚡ Bulk Apply
                <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 8, background: "#bae6fd", color: "#1e40af" }}>
                  {displayKpis.filter(k => k.hasUnits).length}
                </span>
              </button>
            )}
          </div>
          <TagFilterBar
            allQuestions={kpiView === "selected" ? selectedKpis : allKpis}
            filterSteps={filterSteps}
            onFilterStepsChange={setFilterSteps}
            pendingIds={pendingIds}
            savedIds={savedIds}
          />
        </div>

        {/* KPI list */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 120px auto auto",
            padding: "9px 18px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9",
          }}>
            {["KPI Title", "Unit", "", ""].map((h, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</span>
            ))}
          </div>

          {displayKpis.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "#94a3b8" }}>
                {search || filterSteps.some(s => s.values?.size) ? "No KPIs match the current filters." : (kpiView === "selected" ? "No KPIs selected/configured yet." : "No KPI data available.")}
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: 620, overflowY: "auto" }}>
              {displayKpis.map((kpi, idx) => (
                <KpiUnitRow
                   key={kpi.kpiId}
                   kpi={kpi}
                   pendingConfig={pendingConfigs.get(kpi.kpiId) ?? null}
                   onConfigure={openEditor}
                   onQuickUnitChange={handleQuickUnitChange}
                   onRequestUnit={(kpiId, title) => {
                     setRequestType("UNIT");
                     setRequestKpiId(kpiId);
                     setRequestKpiTitle(title ?? null);
                     setShowKpiRequest(true);
                   }}
                   isLast={idx === displayKpis.length - 1}
                   disabled={isViewOnly}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pending footer */}
        {hasPending && !isViewOnly && (
          <div style={{
            marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, padding: "12px 20px",
          }}>
            <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
              {pendingConfigs.size} unsaved change{pendingConfigs.size !== 1 ? "s" : ""} — click Save All to persist.
            </div>
            <button onClick={handleSaveAll} disabled={saving} style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: "#3f88a5", color: "#fff", cursor: "pointer",
              fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 8,
            }}>
              {saving ? <><Spinner size={11} color="#fff" /> Saving…</> : "Save All"}
            </button>
          </div>
        )}
      </div>

      {editingKpi && (
        <UnitEditorModal
          kpi={editingKpi}
          onClose={() => setEditingKpi(null)}
          onSave={handleModalSave}
          refreshKey={unitRequestsRefreshKey}
          onRequestUnit={() => {
            setRequestType("UNIT");
            setRequestKpiId(editingKpi.kpiId);
            setRequestKpiTitle(editingKpi.title ?? null);
            setShowKpiRequest(true);
          }}
        />
      )}
      {showBulkModal && (
        <BulkApplyModal filteredKpis={filteredKpis} onClose={() => setShowBulkModal(false)} onApply={handleBulkApply} />
      )}
      <KpiRequestModal
        show={showKpiRequest}
        onHide={() => setShowKpiRequest(false)}
        defaultType={requestType}
        kpiId={requestKpiId}
        kpiTitle={requestKpiTitle}
        onSuccess={() => setUnitRequestsRefreshKey(prev => prev + 1)}
      />
      <KpiRequestStatusModal
        show={showKpiStatus}
        onHide={() => setShowKpiStatus(false)}
        allowedTypes={["UNIT"]}
        kpiList={allKpis}
      />
    </div>
  );
}